import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import {
  ECheckType,
  ICheckResult,
  IRoleType
} from '@publishing-engine/domain/models';
import { RunDraftIntegrityChecks } from 'src/api/mutations';
import { fetchDraftIntegrityChecks } from 'src/api/queries';
import {
  EIntegrityGate,
  IIntegrityTally,
  getIntegrityGate,
  isRunInProgress,
  shouldBlockSubmit,
  tallyOccurrences,
  toAuthorView
} from './authorIntegrityRules';

/** The checks the service runs for the author on a submission still in draft. */
export const AUTHOR_INTEGRITY_ROLE: IRoleType = 'author';

const POLL_INTERVAL_MS = 10_000;
/** Matches the panel in the Integrity tab; the service's own vendor poll is shorter. */
const RUN_TIMEOUT_MS = 20 * 60 * 1000;

export type IAuthorIntegrityChecks = {
  configuredChecks: ECheckType[];
  /** Every check the role ran, in the order the service returned them. */
  checks: ICheckResult[];
  tally: IIntegrityTally;
  gate: EIntegrityGate;
  /**
   * Whether the submission has to wait. A result set we could not read is not
   * evidence that the checks failed, so a request that errored lets the author
   * through rather than blocking them on a hiccup.
   */
  blocksSubmit: boolean;
  /** The run request itself is in flight. */
  isStarting: boolean;
  isFetching: boolean;
  hasError: boolean;
  startRun: () => Promise<{ isSuccess: boolean; message?: unknown }>;
  refresh: () => void;
};

/**
 * Integrity checks for a draft submission, from the author's point of view.
 *
 * Running a check only tells the vendor to start it, so the panel watches the
 * check rows themselves: while any row is still queued or processing, the
 * draft-checks endpoint is re-read every few seconds. The poll stops as soon as
 * the rows settle, when the tab is hidden, and when the panel unmounts.
 */
export const useAuthorIntegrityChecks = (
  submissionId: string,
  configuredChecks: ECheckType[]
): IAuthorIntegrityChecks => {
  const hasConfiguredChecks = configuredChecks.length > 0;
  // A run that never settles would otherwise be polled forever.
  const pollingDeadline = useRef<number | null>(null);

  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: [
      'draft-integrity-checks',
      { submissionId, role: AUTHOR_INTEGRITY_ROLE }
    ],
    queryFn: () =>
      fetchDraftIntegrityChecks(submissionId, AUTHOR_INTEGRITY_ROLE),
    enabled: Boolean(submissionId) && hasConfiguredChecks,
    retry: 0,
    // React Query v4 hands this the current data and re-runs it on every tick.
    refetchInterval: response => {
      const checks = response?.result.flatMap(result => result.checkResults);
      if (!checks || !isRunInProgress(checks)) {
        pollingDeadline.current = null;
        return false;
      }

      if (pollingDeadline.current === null) {
        pollingDeadline.current = Date.now() + RUN_TIMEOUT_MS;
      }

      return Date.now() < pollingDeadline.current ? POLL_INTERVAL_MS : false;
    },
    refetchIntervalInBackground: false
  });

  const { mutateAsync: runDraftChecks, isPending } = RunDraftIntegrityChecks(
    submissionId,
    AUTHOR_INTEGRITY_ROLE
  );

  const fetchedChecks =
    data?.result.flatMap(result => result.checkResults) ?? [];

  const startRun = async () => {
    // A new run resets the window the previous one may have exhausted.
    pollingDeadline.current = null;

    try {
      return await runDraftChecks();
    } catch (_error) {
      return { isSuccess: false, message: undefined };
    }
  };

  const gate = getIntegrityGate(fetchedChecks, configuredChecks);

  return {
    configuredChecks,
    // The author view only withdraws a flag, so the tally and the gate read the
    // same rows either way.
    checks: toAuthorView(fetchedChecks),
    tally: tallyOccurrences(fetchedChecks),
    gate,
    blocksSubmit: shouldBlockSubmit(gate, isError),
    isStarting: isPending,
    isFetching,
    hasError: isError,
    startRun,
    refresh: refetch
  };
};
